import { FC, ReactElement } from 'react';

import classNames from 'classnames';

import { InputProps } from '../../../components';
import { FormOption } from '../../../entities/@forms/Form/Form';
import { ProductVariantColor } from '../../../entities/@products/ProductVariantColor/ProductVariantColor';
import { transformToProductVariantColorVariable } from '../../../entities/@products/ProductVariantColor/ProductVariantColorTransformers';
import { Checkbox, InputLabelProps, InputLabelWrapper } from '../..';

import './CheckboxList.scss';

interface CheckboxListProps extends InputLabelProps, Omit<InputProps, 'onChange'> {
    isSmall?: boolean;
    showColorIndicator?: boolean;
    name: string;
    value: string[];
    options: FormOption[];
    onChange: (values: string[]) => void;
    className?: string;
}

export const CheckboxList: FC<CheckboxListProps> = ({
    isSmall,
    showColorIndicator,
    id,
    label,
    hideLabel,
    value = [],
    options,
    tooltip,
    required,
    error,
    onChange,
    className = '',
    ...inputProps
}): ReactElement => {
    const handleCheckboxChange = (option: FormOption, isChecked: boolean): void => {
        const newCheckedOptions = isChecked
            ? [...value, option.value]
            : [...value].filter(selectedOption => selectedOption !== option.value);

        onChange(newCheckedOptions);
    };

    const checkboxListClassNames = classNames('checkbox-list', {
        'checkbox-list--is-small': isSmall,
    }, className);

    const checkboxListOptionWrapperClassNames = classNames('checkbox-list__option-wrapper', {
        'checkbox-list__option-wrapper--has-error': error,
    });

    return (
        <div id={id} className={checkboxListClassNames}>
            <InputLabelWrapper
                required={required}
                hideLabel={hideLabel}
                label={label}
                tooltip={tooltip}
                error={error}
                labelClassName="checkbox-list__label"
            >
                <div className={checkboxListOptionWrapperClassNames}>
                    {options.map(option => {
                        const handleSelect = (isChecked: boolean): void => handleCheckboxChange(option, isChecked);
                        const optionColor = showColorIndicator
                            ? transformToProductVariantColorVariable(option.value as ProductVariantColor)
                            : undefined;

                        return (
                            <Checkbox
                                {...inputProps}
                                key={option.value}
                                isSmall={isSmall}
                                label={option.label}
                                value={option.value}
                                checked={value.includes(option.value)}
                                colorIndicator={optionColor}
                                onChange={handleSelect}
                                className="checkbox-list__item"
                            />
                        );
                    })}
                </div>
            </InputLabelWrapper>
        </div>
    );
};
